import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-input-date-picker WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, InputDatePickerWebElement.ts.
 */
export class InputDatePickerWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>columnSpan</code> property.
   * Specifies how many columns this component should span.
   * @return The value of <code>columnSpan</code> property.
   *
   */
  public getColumnSpan(): Promise<number> {
    return this.getProperty<number>('columnSpan');
  }

  /**
   * Gets the value of <code>containerReadonly</code> property.
   * Specifies whether an ancestor container, like oj-c-form-layout, is readonly.
   * @return The value of <code>containerReadonly</code> property.
   *
   */
  public getContainerReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('containerReadonly');
  }

  /**
   * Gets the value of <code>dateRangeOverflowMessageDetail</code> property.
   * Overrides the default validator's rangeOverflow message detail.
   * @return The value of <code>dateRangeOverflowMessageDetail</code> property.
   *
   */
  public getDateRangeOverflowMessageDetail(): Promise<null> {
    return this.getProperty<null>('dateRangeOverflowMessageDetail');
  }

  /**
   * Gets the value of <code>dateRangeUnderflowMessageDetail</code> property.
   * Overrides the default validator's rangeUnderflow message detail.
   * @return The value of <code>dateRangeUnderflowMessageDetail</code> property.
   *
   */
  public getDateRangeUnderflowMessageDetail(): Promise<null> {
    return this.getProperty<null>('dateRangeUnderflowMessageDetail');
  }

  /**
   * Gets the value of <code>dateRestrictionMessageDetail</code> property.
   * Overrides the default validator's rangeUnderflow message detail.
   * @return The value of <code>dateRestrictionMessageDetail</code> property.
   *
   */
  public getDateRestrictionMessageDetail(): Promise<null> {
    return this.getProperty<null>('dateRestrictionMessageDetail');
  }

  /**
   * Gets the value of <code>dayFormatter</code> property.
   * Provides additional information for rendering a day.
   * @return The value of <code>dayFormatter</code> property.
   *
   */
  public getDayFormatter(): Promise<null> {
    return this.getProperty<null>('dayFormatter');
  }

  /**
   * Gets the value of <code>daysOutsideMonth</code> property.
   * Specifies the behavior of days outside of the current month in the Date Picker.
   * @return The value of <code>daysOutsideMonth</code> property.
   *
   */
  public getDaysOutsideMonth(): Promise<string> {
    return this.getProperty<string>('daysOutsideMonth');
  }

  /**
   * Gets the value of <code>disabled</code> property.
   * Specifies whether the component is disabled.
   * @return The value of <code>disabled</code> property.
   *
   */
  public getDisabled(): Promise<boolean> {
    return this.getProperty<boolean>('disabled');
  }

  /**
   * Gets the value of <code>displayOptions</code> property.
   * Display options for auxiliary content that determines whether or not it should be displayed.
   * @return The value of <code>displayOptions</code> property.
   *
   */
  public getDisplayOptions(): Promise<DisplayOptions> {
    return this.getProperty<DisplayOptions>('displayOptions');
  }

  /**
   * Gets the value of <code>help</code> property.
   * Form component help information.
   * @return The value of <code>help</code> property.
   *
   */
  public getHelp(): Promise<Help> {
    return this.getProperty<Help>('help');
  }

  /**
   * Gets the value of <code>helpHints</code> property.
   * The helpHints object contains a definition property and a source property.
   * @return The value of <code>helpHints</code> property.
   *
   */
  public getHelpHints(): Promise<HelpHints> {
    return this.getProperty<HelpHints>('helpHints');
  }

  /**
   * Gets the value of <code>labelEdge</code> property.
   * Specifies how the label is positioned for the component
   * @return The value of <code>labelEdge</code> property.
   *
   */
  public getLabelEdge(): Promise<string> {
    return this.getProperty<string>('labelEdge');
  }

  /**
   * Gets the value of <code>labelHint</code> property.
   * Represents a hint for rendering a label on the component.
   * @return The value of <code>labelHint</code> property.
   *
   */
  public getLabelHint(): Promise<string> {
    return this.getProperty<string>('labelHint');
  }

  /**
   * Gets the value of <code>labelStartWidth</code> property.
   * The width of the label when labelEdge is 'start'.
   * @return The value of <code>labelStartWidth</code> property.
   *
   */
  public getLabelStartWidth(): Promise<number | string> {
    return this.getProperty<number | string>('labelStartWidth');
  }

  /**
   * Gets the value of <code>labelWrapping</code> property.
   * Should the labels wrap or truncate when there is not enough available space.
   * @return The value of <code>labelWrapping</code> property.
   * @deprecated Since 18.0.0. Label truncation for 'start' and 'top' aligned labels is no longer recommended by the Redwood Design System. The default for labelWrapping was 'wrap' and that is now the only suggested pattern by UX design for 'start' and 'top' aligned labels. 'inside' aligned labels are always truncated per UX design and are not affected by this property's value.
   */
  public getLabelWrapping(): Promise<string> {
    return this.getProperty<string>('labelWrapping');
  }

  /**
   * Gets the value of <code>max</code> property.
   * The maximum selectable date, in ISO string format
   * @return The value of <code>max</code> property.
   *
   */
  public getMax(): Promise<string | null> {
    return this.getProperty<string | null>('max');
  }

  /**
   * Gets the value of <code>maxWidth</code> property.
   * The max width of the control.
   * @return The value of <code>maxWidth</code> property.
   *
   */
  public getMaxWidth(): Promise<number | string> {
    return this.getProperty<number | string>('maxWidth');
  }

  /**
   * Sets the value of <code>messagesCustom</code> property.
   * List of custom component messages
   * @param messagesCustom The value to set for <code>messagesCustom</code>
   *
   */
  public changeMessagesCustom(messagesCustom: Array<MessagesCustom>): Promise<void> {
    return this.setProperty<Array<MessagesCustom>>('messagesCustom', messagesCustom);
  }

  /**
   * Gets the value of <code>messagesCustom</code> property.
   * List of custom component messages
   * @return The value of <code>messagesCustom</code> property.
   *
   */
  public getMessagesCustom(): Promise<Array<MessagesCustom>> {
    return this.getProperty<Array<MessagesCustom>>('messagesCustom');
  }

  /**
   * Gets the value of <code>min</code> property.
   * The minimum selectable date, in ISO string format
   * @return The value of <code>min</code> property.
   *
   */
  public getMin(): Promise<string | null> {
    return this.getProperty<string | null>('min');
  }

  /**
   * Gets the value of <code>monthAndYearPicker</code> property.
   * Specifies whether or not the DatePicker should show the toggle buttons that toggle the month grid picker and year grid picker.
   * @return The value of <code>monthAndYearPicker</code> property.
   *
   */
  public getMonthAndYearPicker(): Promise<string> {
    return this.getProperty<string>('monthAndYearPicker');
  }

  /**
   * Gets the value of <code>readonly</code> property.
   * Whether the component is readonly
   * @return The value of <code>readonly</code> property.
   *
   */
  public getReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('readonly');
  }

  /**
   * Gets the value of <code>readonlyUserAssistanceShown</code> property.
   * Specifies which user assistance types should be shown when the component is readonly.
   * @return The value of <code>readonlyUserAssistanceShown</code> property.
   *
   */
  public getReadonlyUserAssistanceShown(): Promise<string> {
    return this.getProperty<string>('readonlyUserAssistanceShown');
  }

  /**
   * Gets the value of <code>required</code> property.
   * Specifies whether or not the component is required.
   * @return The value of <code>required</code> property.
   *
   */
  public getRequired(): Promise<boolean> {
    return this.getProperty<boolean>('required');
  }

  /**
   * Gets the value of <code>requiredMessageDetail</code> property.
   * Overrides the default Required error message.
   * @return The value of <code>requiredMessageDetail</code> property.
   *
   */
  public getRequiredMessageDetail(): Promise<string> {
    return this.getProperty<string>('requiredMessageDetail');
  }

  /**
   * Gets the value of <code>textAlign</code> property.
   * Specifies how the text is aligned within the text field
   * @return The value of <code>textAlign</code> property.
   *
   */
  public getTextAlign(): Promise<string> {
    return this.getProperty<string>('textAlign');
  }

  /**
   * Gets the value of <code>todayTimeZone</code> property.
   * The time zone used for computing today for the Date Picker.
   * @return The value of <code>todayTimeZone</code> property.
   *
   */
  public getTodayTimeZone(): Promise<string> {
    return this.getProperty<string>('todayTimeZone');
  }

  /**
   * Gets the value of <code>todayButton</code> property.
   * Specifies how to render the 'Go to today' button.
   * @return The value of <code>todayButton</code> property.
   *
   */
  public getTodayButton(): Promise<string> {
    return this.getProperty<string>('todayButton');
  }

  /**
   * Gets the value of <code>userAssistanceDensity</code> property.
   * Specifies the density of the form component's user assistance presentation.
   * @return The value of <code>userAssistanceDensity</code> property.
   *
   */
  public getUserAssistanceDensity(): Promise<string> {
    return this.getProperty<string>('userAssistanceDensity');
  }

  /**
   * Gets the value of <code>validators</code> property.
   * Specifies the validators for the component.
   * @return The value of <code>validators</code> property.
   *
   */
  public getValidators(): Promise<Array<object> | null> {
    return this.getProperty<Array<object> | null>('validators');
  }

  /**
   * Sets the value of <code>value</code> property.
   * The value of the component.
   * @param value The value to set for <code>value</code>
   *
   */
  public changeValue(value: string | null): Promise<void> {
    return this.setProperty<string | null>('value', value);
  }

  /**
   * Gets the value of <code>value</code> property.
   * The value of the component.
   * @return The value of <code>value</code> property.
   *
   */
  public getValue(): Promise<string | null> {
    return this.getProperty<string | null>('value');
  }

  /**
   * Gets the value of <code>weekDisplay</code> property.
   * Whether week of the year will be shown in the DatePicker.
   * @return The value of <code>weekDisplay</code> property.
   *
   */
  public getWeekDisplay(): Promise<string> {
    return this.getProperty<string>('weekDisplay');
  }

  /**
   * Gets the value of <code>width</code> property.
   * The width of the control.
   * @return The value of <code>width</code> property.
   *
   */
  public getWidth(): Promise<number | string> {
    return this.getProperty<number | string>('width');
  }

  /**
   * Gets the value of <code>rawValue</code> property.
   * Specifies how the raw value of the component
   * @return The value of <code>rawValue</code> property.
   *
   */
  public getRawValue(): Promise<RawValue> {
    return this.getProperty<RawValue>('rawValue');
  }

  /**
   * Gets the value of <code>valid</code> property.
   * Specifies how the valid state of the component
   * @return The value of <code>valid</code> property.
   *
   */
  public getValid(): Promise<string> {
    return this.getProperty<string>('valid');
  }
}
export interface DisplayOptions {
  /**
   *
   */
  messages: string;
  /**
   *
   */
  validatorHint: string;
}
export interface Help {
  /**
   *
   */
  instruction: string;
}
export interface HelpHints {
  /**
   *
   */
  definition: string;
  /**
   *
   */
  source: string;
  /**
   *
   */
  sourceText: string;
}
export interface MessagesCustom {
  /**
   *
   */
  summary: string;
  /**
   *
   */
  detail: string;
  /**
   *
   */
  severity: string;
}
export interface RawValue {
  /**
   *
   */
  year: number;
  /**
   *
   */
  month: number;
  /**
   *
   */
  day: number;
}
